import apiClient from '@/api/apiClient';

export interface RelationshipTypeOptionResponse {
  id: string;
  name: string;
  slug: string;
  entity_types: string[];
}

// Where a parecer's relationship-type options came from: the report's policy, or the account
// (fallback for policies that define none).
export type ParecerRelationshipTypesSource = 'policy' | 'account';

export interface ParecerRelationshipTypes {
  source: ParecerRelationshipTypesSource;
  relationshipTypes: RelationshipTypeOptionResponse[];
}

const relationshipTypesApi = {
  getAvailableRelationshipTypes(entityType?: 'person' | 'company') {
    return apiClient.get<RelationshipTypeOptionResponse[]>('/account/relationship-types', {
      params: { entity_type: entityType },
    });
  },
  getRelationshipTypesForRiskProfile(riskProfileId: string) {
    return apiClient.get<RelationshipTypeOptionResponse[]>(
      `/account/relationship-types/for-risk-profile/${riskProfileId}`,
    );
  },
  getRelationshipTypes() {
    return apiClient.get('/account-admin/relationship-types');
  },
  // The adoptable subset only (active + not yet adopted). Account admins never get the full catalog.
  getConfigRelationshipTypes() {
    return apiClient.get('/account-admin/relationship-types/config-relationship-types');
  },
  createRelationshipType(data: any) {
    return apiClient.post('/account-admin/relationship-types', data);
  },
  updateRelationshipType(slug: string, data: any) {
    return apiClient.patch(`/account-admin/relationship-types/${slug}`, data);
  },
  // The predefined catalog is global; only system admins may view it for management or edit it.
  getSystemConfigRelationshipTypes() {
    return apiClient.get('/system-admin/relationship-types/config-relationship-types');
  },
  updateConfigRelationshipType(slug: string, data: any) {
    return apiClient.patch(`/system-admin/relationship-types/config/${slug}`, data);
  },
  deleteRelationshipType(id: string) {
    return apiClient.delete(`/account-admin/relationship-types/${id}`);
  },

  async getParecerRelationshipTypes(
    riskProfileId: string,
    entityType: 'person' | 'company',
  ): Promise<ParecerRelationshipTypes> {
    const policyRelationshipTypes = await relationshipTypesApi.getRelationshipTypesForRiskProfile(riskProfileId);
    if (policyRelationshipTypes.length > 0) {
      return { source: 'policy', relationshipTypes: policyRelationshipTypes };
    }

    const accountRelationshipTypes = await relationshipTypesApi.getAvailableRelationshipTypes(entityType);
    return { source: 'account', relationshipTypes: accountRelationshipTypes };
  },
};

export default relationshipTypesApi;
